import { useCallback, useEffect, useMemo, useState } from "react";
import {
  commitPriceChange,
  getPriceChangeCatalog,
  getPriceChangeSnapshot,
  listPriceChangeSnapshots,
  savePriceChangeDraft,
  type PriceChangeSnapshotSummary,
} from "@/lib/manufacturing/priceChangeSnapshotClient";
import {
  countPriceChangeConfirmations,
  countPriceChangeEdits,
  EMPTY_PRICE_CHANGE_NARROW,
  filterPriceChangeLines,
  parsePriceChangeMoney,
  priceChangeFacetLabels,
  type PriceChangeLine,
  type PriceChangeNarrowFilters,
  type PriceChangeStatus,
} from "@/lib/manufacturing/priceChangeSnapshot";
import {
  applyPriceChangeImport,
  parsePriceChangeCsv,
  parsePriceChangeJson,
  priceChangeLinesToCsv,
} from "@/lib/manufacturing/priceChangeSnapshotFile";
import { applyPriceChangeStyles } from "@/lib/manufacturing/priceChangeStyle";
import { priceChangeSnapshotPdf } from "@/lib/manufacturing/priceChangeSnapshotPdf";

const PAGE_SIZE = 25;

function slug(name: string): string {
  const text = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  return text || "price-change";
}

function downloadBlob(filename: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function useManufacturingModelsPriceChange(onCommitted?: (applied: number) => void) {
  const [snapshotId, setSnapshotId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState<PriceChangeStatus | "local">("local");
  const [committedAt, setCommittedAt] = useState<string | null>(null);
  const [lines, setLines] = useState<PriceChangeLine[]>([]);
  const [summaries, setSummaries] = useState<PriceChangeSnapshotSummary[]>([]);
  const [search, setSearch] = useState("");
  const [group, setGroup] = useState("");
  const [roof, setRoof] = useState("");
  const [narrow, setNarrow] = useState<PriceChangeNarrowFilters>(EMPTY_PRICE_CHANGE_NARROW);
  const [page, setPage] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  const locked = status === "committed";
  const filtered = useMemo(
    () => filterPriceChangeLines(lines, { search, group, roof, ...narrow }),
    [lines, search, group, roof, narrow],
  );
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageLines = filtered.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);
  const groups = useMemo(() => priceChangeFacetLabels(lines, "group_name"), [lines]);
  const roofs = useMemo(() => priceChangeFacetLabels(lines, "roof_style"), [lines]);
  const kinds = useMemo(() => priceChangeFacetLabels(lines, "kind"), [lines]);
  const editCount = countPriceChangeEdits(lines);
  const confirmation = useMemo(() => countPriceChangeConfirmations(lines), [lines]);

  const refreshList = useCallback(async () => {
    const listed = await listPriceChangeSnapshots();
    if (!listed.ok) {
      setError(listed.error);
      return;
    }
    setSummaries(listed.data.snapshots);
  }, []);

  const loadCatalog = useCallback(async () => {
    setBusy(true);
    setError(null);
    const catalog = await getPriceChangeCatalog();
    setBusy(false);
    if (!catalog.ok) {
      setError(catalog.error);
      return false;
    }
    setSnapshotId(null);
    setName("");
    setNotes("");
    setStatus("local");
    setCommittedAt(null);
    setLines(applyPriceChangeStyles(catalog.data.lines));
    setSearch("");
    setGroup("");
    setRoof("");
    setNarrow(EMPTY_PRICE_CHANGE_NARROW);
    setPage(0);
    setDirty(false);
    setNotice(`${catalog.data.lines.length} products, options, and packages.`);
    return true;
  }, []);

  useEffect(() => {
    void refreshList();
    void loadCatalog();
  }, [refreshList, loadCatalog]);

  function confirmDiscard(): boolean {
    if (!dirty) return true;
    return window.confirm("Discard unsaved edits in this price change?");
  }

  async function startNew() {
    if (!confirmDiscard()) return;
    await loadCatalog();
  }

  async function openSaved(id: string) {
    if (!id) return;
    if (!confirmDiscard()) return;
    setBusy(true);
    setError(null);
    const loaded = await getPriceChangeSnapshot(id);
    setBusy(false);
    if (!loaded.ok) {
      setError(loaded.error);
      return;
    }
    const snap = loaded.data.snapshot;
    setSnapshotId(snap.id);
    setName(snap.name);
    setNotes(snap.notes);
    setStatus(snap.status);
    setCommittedAt(snap.committed_at);
    setLines(applyPriceChangeStyles(snap.lines));
    setPage(0);
    setDirty(false);
    setNotice(snap.status === "committed" ? "Committed history. Prices are not edited from here." : "Draft opened.");
  }

  function setNewPrice(recipeId: string, field: "new_wholesale_usd" | "new_msrp_usd", raw: string) {
    if (locked) return;
    const parsed = parsePriceChangeMoney(raw);
    if (!parsed.ok) {
      setError(parsed.error);
      return;
    }
    setError(null);
    const confirmedField = field === "new_wholesale_usd" ? "new_wholesale_confirmed" : "new_msrp_confirmed";
    setLines((prev) =>
      prev.map((line) => {
        if (line.recipe_id !== recipeId || line[field] === parsed.value) return line;
        return { ...line, [field]: parsed.value, [confirmedField]: false };
      }),
    );
    setDirty(true);
  }

  function setConfirmed(
    recipeId: string,
    field: "new_wholesale_confirmed" | "new_msrp_confirmed",
    checked: boolean,
  ) {
    if (locked) return;
    const priceField = field === "new_wholesale_confirmed" ? "new_wholesale_usd" : "new_msrp_usd";
    setLines((prev) =>
      prev.map((line) => {
        if (line.recipe_id !== recipeId || line[priceField] == null) return line;
        return { ...line, [field]: checked };
      }),
    );
    setDirty(true);
  }

  async function persist(): Promise<string | null> {
    const saved = await savePriceChangeDraft({
      id: snapshotId ?? undefined,
      name,
      notes,
      lines,
    });
    if (!saved.ok) {
      setError(saved.error);
      return null;
    }
    setSnapshotId(saved.data.snapshot.id);
    setStatus(saved.data.snapshot.status);
    setDirty(false);
    await refreshList();
    return saved.data.snapshot.id;
  }

  async function save() {
    if (locked) return;
    setBusy(true);
    setError(null);
    const id = await persist();
    setBusy(false);
    if (id) setNotice("Saved.");
  }

  async function commit() {
    if (locked) return;
    if (!editCount) {
      setError("Enter at least one new wholesale or MSRP before committing.");
      return;
    }
    if (!window.confirm(`Write ${editCount} new prices onto the catalog and keep this program in history?`)) return;
    setBusy(true);
    setError(null);
    const id = await persist();
    if (!id) {
      setBusy(false);
      return;
    }
    const out = await commitPriceChange(id);
    setBusy(false);
    if (!out.ok) {
      setError(out.error);
      return;
    }
    setStatus("committed");
    setCommittedAt(out.data.snapshot.committed_at);
    setLines(applyPriceChangeStyles(out.data.snapshot.lines));
    setNotice(`Committed. ${out.data.applied} shelf prices updated.`);
    await refreshList();
    onCommitted?.(out.data.applied);
  }

  async function importFile(file: File) {
    if (locked) return;
    const text = await file.text();
    const parsed = file.name.toLowerCase().endsWith(".json") ? parsePriceChangeJson(text) : parsePriceChangeCsv(text);
    if (!parsed.ok) {
      setError(parsed.error);
      return;
    }
    const applied = applyPriceChangeImport(lines, parsed.rows);
    setLines(applyPriceChangeStyles(applied.lines));
    setDirty(true);
    const importedName = "name" in parsed && typeof parsed.name === "string" ? parsed.name : "";
    const importedNotes = "notes" in parsed && typeof parsed.notes === "string" ? parsed.notes : "";
    if (importedName && !name.trim()) setName(importedName);
    if (importedNotes && !notes.trim()) setNotes(importedNotes);
    const unknown = applied.unknown.slice(0, 4).join(", ");
    setNotice(
      `Updated ${applied.updated} new prices.${unknown ? ` Codes not in this program: ${unknown}.` : ""}`,
    );
    setError(null);
  }

  function downloadCsv() {
    downloadBlob(
      `${slug(name)}-filtered.csv`,
      new Blob([priceChangeLinesToCsv(filtered)], { type: "text/csv;charset=utf-8" }),
    );
  }

  function downloadJson() {
    const body = JSON.stringify({ name, notes, lines: filtered }, null, 2);
    downloadBlob(`${slug(name)}-filtered.json`, new Blob([body], { type: "application/json" }));
  }

  function downloadPdf() {
    const bytes = priceChangeSnapshotPdf({ name: name || "Price change", notes, lines, committedAt });
    downloadBlob(`${slug(name)}.pdf`, new Blob([bytes as unknown as BlobPart], { type: "application/pdf" }));
  }

  return {
    snapshotId,
    name,
    setName: (value: string) => {
      setName(value);
      if (!locked) setDirty(true);
    },
    notes,
    setNotes: (value: string) => {
      setNotes(value);
      if (!locked) setDirty(true);
    },
    status,
    committedAt,
    locked,
    lines,
    filteredCount: filtered.length,
    pageLines,
    page: safePage,
    pageCount,
    setPage,
    summaries,
    search,
    setSearch: (value: string) => {
      setSearch(value);
      setPage(0);
    },
    group,
    setGroup: (value: string) => {
      setGroup(value);
      setPage(0);
    },
    roof,
    setRoof: (value: string) => {
      setRoof(value);
      setPage(0);
    },
    groups,
    roofs,
    kinds,
    narrow,
    setNarrowField: (key: keyof PriceChangeNarrowFilters, value: string) => {
      setNarrow((prev) => ({ ...prev, [key]: value }) as PriceChangeNarrowFilters);
      setPage(0);
    },
    editCount,
    confirmedCount: confirmation.confirmed,
    confirmTotal: confirmation.total,
    busy,
    error,
    notice,
    dirty,
    startNew,
    openSaved,
    save,
    commit,
    importFile,
    downloadCsv,
    downloadJson,
    downloadPdf,
    setNewPrice,
    setConfirmed,
  };
}
